import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import "./styles/global.css";
import "./styles/zindex.css";
import "./styles/Toast.css";
import App from "./App";
import { AccessibilityProvider } from "./context/AccessibilityContext";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { ToastProvider } from "./context/ToastContext";
import { ConfirmDialogProvider } from "./context/ConfirmDialogContext";

// Wycisz odrzucenia axios (401/anulowane/brak sieci) — nie mają wywalać overlaya CRA
// (wzorzec z mpracownika).
window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    if (!reason) return;
    const status = reason.response?.status;
    const isAxios = reason.isAxiosError === true;
    if (isAxios && (!reason.response || status === 401 || [502, 503, 504].includes(status))) {
        event.preventDefault();
    }
    if (reason.code === "ERR_CANCELED") {
        event.preventDefault();
    }
});

/**
 * OKNO POWROTU LOGOWANIA MICROSOFT (popup MSAL).
 *
 * Popup wraca z login.microsoftonline.com na nasz origin z odpowiedzią w HASHU adresu
 * (#code=…&state=…). Okno-RODZIC odczytuje ten hash interwałem — i dopiero ono wymienia
 * kod na sesję. W popupie NIE WOLNO wtedy uruchamiać aplikacji: router od pierwszego
 * renderu podmienia adres (Navigate replace na /logowanie), kasując hash. Na komputerze
 * rodzic zwykle zdąży go przeczytać, ale na telefonie interwał karty w tle jest
 * wstrzymany aż do powrotu na nią — hash ginie ZAWSZE i MSAL zgłasza
 * „Hash value cannot be processed because it is empty" (hash_empty_error).
 *
 * Dlatego adres z odpowiedzią MSAL dostaje ekran przejściowy zamiast SPA — hash zostaje
 * nietknięty, rodzic czyta go w swoim tempie i sam zamyka popup. Wzorzec zalecany przez
 * MSAL („redirectUri powinien wskazywać stronę bez logiki routingu") — bez zmian
 * w rejestracji aplikacji Azure, bo adres pozostaje ten sam.
 *
 * Warunek jest celowo wąski: `code=` LUB `error=` ORAZ `state=` w hashu. Aplikacja sama
 * nigdy nie używa takich parametrów w hashu (tokeny podróżują w query „?token=…").
 */
const authHash = window.location.hash || "";
const isMsalAuthReturn = /[#&](code|error)=/.test(authHash) && /[#&]state=/.test(authHash);

if (isMsalAuthReturn) {
    // Bez Reacta i routera — czysty HTML, żaden kod nie ma prawa dotknąć adresu.
    // Zmienne --color-* są dostępne: global.css jest już zaimportowany, a motyw ustawia
    // skrypt w index.html przed tym plikiem.
    document.getElementById("root").innerHTML = `
        <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;
                    padding:24px;background:var(--color-bg-secondary);
                    font-family:var(--font-family-main,sans-serif)">
            <div style="max-width:360px;text-align:center;color:var(--color-text-primary)">
                <div style="width:34px;height:34px;margin:0 auto 16px;border-radius:50%;
                            border:3px solid var(--color-border);border-top-color:#6366F1;
                            animation:bp-spin .8s linear infinite"></div>
                <h1 style="font-size:1.02rem;font-weight:700;margin:0 0 8px">
                    Wracamy do aplikacji…
                </h1>
                <p style="font-size:.83rem;line-height:1.55;margin:0;color:var(--color-text-secondary)">
                    Przekazujemy odpowiedź logowania Microsoft — to okno zamknie się samo.
                    Jeśli tak się nie stanie, wróć na kartę, z której rozpoczęto logowanie:
                    tam dokończy się ono automatycznie.
                </p>
            </div>
            <style>@keyframes bp-spin{to{transform:rotate(360deg)}}</style>
        </div>`;
} else {
    const root = ReactDOM.createRoot(document.getElementById("root"));
    // ToastProvider NAJBARDZIEJ NA ZEWNĄTRZ (wzorzec mpracownika) — toasty przeżywają zmianę
    // trasy i błędy wewnątrz drzewa. ConfirmDialogProvider tuż pod nim, żeby confirm() był
    // dostępny wszędzie (jego overlay jest na warstwie 100000, czyli ponad toastami).
    root.render(
        <React.StrictMode>
            <ToastProvider>
                <ConfirmDialogProvider>
                    <BrowserRouter>
                        <ThemeProvider>
                            {/* Dostępność (WCAG) obok motywu: działa też PRZED zalogowaniem
                                (logowanie, portal gościa); mówi do CSS atrybutami na <html>. */}
                            <AccessibilityProvider>
                                <AuthProvider>
                                    <App />
                                </AuthProvider>
                            </AccessibilityProvider>
                        </ThemeProvider>
                    </BrowserRouter>
                </ConfirmDialogProvider>
            </ToastProvider>
        </React.StrictMode>
    );
}
